import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Display-only overlay for an input whose real value lives in the entity's field record.
 *
 * The field record is the backing state: the input renders `draft ?? fieldValue` and writes
 * every change straight through to the record from its event handlers. The draft exists only
 * to hold text that wouldn't survive a round-trip through the field — a trailing separator, a
 * half-typed number — and it must never outlive the field.
 *
 * So when the field changes to a value this input didn't write, the draft is dropped and the
 * field wins. That happens when the entity finishes loading, when the card modal swaps to
 * another entity, and when another user's edit is accepted from the conflict banner. Without
 * it the input goes on showing a value the card no longer holds.
 *
 * Writes go through the returned setter, which records what was committed so the same value
 * arriving back through `fieldValue` is recognised as our own and leaves the draft alone.
 *
 * `TDraft` is what the input displays, `TCommitted` what gets stored. They differ exactly when
 * committing normalizes the value (trimming, parsing) — which is when the draft has to survive.
 */
export function useFieldDraft<TDraft, TCommitted>(
  fieldValue: TCommitted,
  isSameCommittedValue: (a: TCommitted, b: TCommitted) => boolean = Object.is,
): [TDraft | null, (draft: TDraft, committed: TCommitted) => void] {
  const [draft, setDraft] = useState<TDraft | null>(null);
  // Boxed so that "never written" stays distinguishable from "wrote an empty value".
  const lastCommitted = useRef<[TCommitted] | null>(null);

  const commitDraft = useCallback((updatedDraft: TDraft, committedValue: TCommitted) => {
    lastCommitted.current = [committedValue];
    setDraft(updatedDraft);
  }, []);

  useEffect(() => {
    const isOwnWrite =
      lastCommitted.current !== null && isSameCommittedValue(lastCommitted.current[0], fieldValue);
    if (!isOwnWrite) {
      setDraft(null);
    }
    // Deliberately keyed on the field alone. `isSameCommittedValue` is an inline comparator
    // whose identity changes every render, and re-running on it would defeat the purpose.
  }, [fieldValue]); // eslint-disable-line react-hooks/exhaustive-deps

  return [draft, commitDraft];
}
